import { deriveSalary, formatCRC, monthlyOvertimeEstimate } from "../lib/salary";

/**
 * Muestra el desglose salarial derivado en vivo.
 * Si la fijación es Mensual: resalta el mensual y deriva diario/hora/extra.
 * Si es Diaria: resalta el diario y deriva mensual/hora/extra.
 */
export function SalaryBreakdown({ amount, salaryType, cfg, testid = "salary-breakdown" }) {
  const d = deriveSalary(amount, salaryType, cfg);
  const isMonthly = salaryType !== "daily";

  const rows = isMonthly
    ? [
        { label: "Salario mensual", value: d.monthly, primary: true },
        { label: "Salario diario", value: d.daily },
        { label: "Salario por hora", value: d.hourly },
        { label: `Hora extra (×${d.overtimeMult})`, value: d.overtimeHour },
      ]
    : [
        { label: "Salario diario", value: d.daily, primary: true },
        { label: "Salario mensual", value: d.monthly },
        { label: "Salario por hora", value: d.hourly },
        { label: `Hora extra (×${d.overtimeMult})`, value: d.overtimeHour },
      ];

  return (
    <div
      className="rounded-lg border border-zinc-200 bg-zinc-50 p-3"
      data-testid={testid}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-2">
        Desglose salarial ({isMonthly ? "Mensual" : "Diaria"})
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {rows.map((r) => (
          <div
            key={r.label}
            className={`flex items-center justify-between ${r.primary ? "col-span-2" : ""}`}
          >
            <span className={`text-xs ${r.primary ? "font-semibold text-zinc-800" : "text-zinc-600"}`}>
              {r.label}
            </span>
            <span
              className={`font-mono tabular-nums ${r.primary ? "text-sm font-bold text-zinc-900" : "text-xs text-zinc-700"}`}
            >
              {formatCRC(r.value)}
            </span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-zinc-400 mt-2 leading-snug">
        Base: 30 días/mes, jornada de 8 h/día (240 h/mes). La hora extra se paga a
        tiempo y medio sobre la jornada ordinaria.
      </p>
    </div>
  );
}


/**
 * Estima la ganancia mensual por horas extra a partir del horario por día.
 *   horas extra/semana × 4.33 semanas/mes × precio de la hora extra.
 */
export function OvertimeBreakdown({
  amount,
  salaryType,
  cfg,
  daySchedules,
  journeyType = "JOD",
  testid = "overtime-breakdown",
}) {
  const d = deriveSalary(amount, salaryType, cfg);
  const est = monthlyOvertimeEstimate({ daySchedules, journeyType, overtimeHour: d.overtimeHour });
  const fmtH = (h) => (Number.isInteger(h) ? String(h) : String(h).replace(".", ","));

  return (
    <div
      className="rounded-lg border border-orange-200 bg-orange-50/60 p-3"
      data-testid={testid}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-orange-700 mb-2">
        Estimado de horas extra (mensual)
      </p>
      {est.weekExtra > 0 ? (
        <>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">Horas extra por semana</span>
              <span className="font-mono tabular-nums text-xs text-zinc-700">{fmtH(est.weekExtra)} h</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">Semanas por mes</span>
              <span className="font-mono tabular-nums text-xs text-zinc-700">× {String(est.weeksPerMonth).replace(".", ",")}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">Horas extra al mes</span>
              <span className="font-mono tabular-nums text-xs text-zinc-700">{fmtH(est.monthlyExtraHours)} h</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">Precio hora extra (×{d.overtimeMult})</span>
              <span className="font-mono tabular-nums text-xs text-zinc-700">{formatCRC(d.overtimeHour)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-orange-200 pt-2 mt-1">
              <span className="text-xs font-semibold text-zinc-800">Total horas extra / mes</span>
              <span className="font-mono tabular-nums text-sm font-bold text-orange-700" data-testid={`${testid}-total`}>
                {formatCRC(est.total)}
              </span>
            </div>
          </div>
          <p className="text-[10px] text-zinc-400 mt-2 leading-snug">
            Estimación según el horario definido: {fmtH(est.weekExtra)} h extra/semana ×{" "}
            {String(est.weeksPerMonth).replace(".", ",")} semanas/mes × {formatCRC(d.overtimeHour)}.
          </p>
        </>
      ) : (
        <p className="text-xs text-zinc-500">
          Según el horario definido, no se generan horas extra semanales (jornada dentro
          del límite de ley).
        </p>
      )}
    </div>
  );
}


/**
 * Total a pagar mensual estimado (salario base + horas extra), SIN rebajos.
 */
export function MonthlyPayEstimate({
  amount,
  salaryType,
  cfg,
  daySchedules,
  journeyType = "JOD",
  testid = "monthly-pay-estimate",
}) {
  const d = deriveSalary(amount, salaryType, cfg);
  const est = monthlyOvertimeEstimate({ daySchedules, journeyType, overtimeHour: d.overtimeHour });
  const total = Math.round((d.monthly + est.total) * 100) / 100;

  return (
    <div
      className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
            Total a pagar mensual
          </p>
          <p className="text-[10px] text-zinc-500">
            Salario base + horas extra estimadas — <b>sin rebajos</b> (antes de CCSS y
            otras deducciones de ley).
          </p>
        </div>
        <span
          className="font-mono tabular-nums text-base font-bold text-emerald-700"
          data-testid={`${testid}-total`}
        >
          {formatCRC(total)}
        </span>
      </div>
    </div>
  );
}
